import { Button, Card, Fieldset, Heading } from '@digdir/designsystemet-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon } from '@navikt/aksel-icons';

import styled from '../../../../../styled';
import { ErrorBox } from '../../../../../components/errorBox';
import { ButtonWrapper } from '../../solceller/form/components/styledComponents';
import { OppvarmingFormData } from '../types';
import { FormContentWrapper } from '../../omBoligen/form';
import { useRegistrationSteps } from '../../../hooks/useRegistrationSteps';
import { RegistrationCommonStep } from '../../../../../utils/navigation';
import { FormBackButton } from '../../../components/formBackButton';

import { FormErrors } from './components/formErrors';
import { Varmepumpe } from './components/varmepumpe';
import { Elektrisitet } from './components/elektrisitet';
import { Ved } from './components/ved';
import { Fjernvarme } from './components/fjernvarme';
import { Gass } from './components/gass';
import { Olje } from './components/olje';
import { Bioenergi } from './components/bioenergi';
import { Solfanger } from './components/solfanger';
import { useOppvarmingSubmit } from './hooks/useOppvarmingSubmit';
import { hasSelectedOppvarmingskilde } from './utils';

const FormCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: var(--ds-size-1);
`;

interface Props {
  defaultValues?: OppvarmingFormData;
}

export const OppvarmingForm: FC<Props> = ({ defaultValues }) => {
  const methods = useForm<OppvarmingFormData>({ defaultValues });
  const navigate = useNavigate();
  const { registrationId } = useParams();
  const { previousStep, nextStep } = useRegistrationSteps(RegistrationCommonStep.HEATING);
  const { onOppvarmingSubmit, isLoading, updateError } = useOppvarmingSubmit();

  const onSubmit = (data: OppvarmingFormData) => {
    if (!hasSelectedOppvarmingskilde(data)) {
      // We display a warning if no oppvarming is used as "Romoppvarming"
      methods.setError('root.romoppvarmingMissing', { type: 'manual' });
      return;
    }
    onOppvarmingSubmit({ data, onSuccess: () => navigate(`../${nextStep}`) });
  };

  const navigateBack = () => navigate(`../${previousStep}`);

  const { t } = useTranslation();

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
        <FormContentWrapper>
          <FormCard>
            <Fieldset disabled={isLoading}>
              <Fieldset.Legend>
                <Heading level={2} data-size="md">
                  {t('form.oppvarming.oppvarmingSelection.heading')}
                </Heading>
              </Fieldset.Legend>
              <Elektrisitet />
              <Fjernvarme />
              <Bioenergi />
              <Varmepumpe />
              <Olje />
              <Solfanger />
              <Ved />
              <Gass />
            </Fieldset>
          </FormCard>

          <FormErrors />

          <ErrorBox error={updateError} registrationId={registrationId} />

          <ButtonWrapper>
            <FormBackButton<OppvarmingFormData>
              isLoading={isLoading}
              onSuccess={navigateBack}
              onSubmit={onOppvarmingSubmit}
            />
            <Button loading={isLoading} type="submit">
              {t('form.common.next')} <ChevronRightIcon />
            </Button>
          </ButtonWrapper>
        </FormContentWrapper>
      </form>
    </FormProvider>
  );
};
